import { useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, Star, Trash2, X, Loader2, ImageOff } from 'lucide-react';
import equipmentService from '../../services/equipment';
import type { EquipmentPhoto } from '../../types/equipment';
import { cn } from '../../utils/cn';

// ─── Photo URLs ──────────────────────────────────────────────────────────────
// The file endpoint needs the bearer token, so each photo is fetched once as a
// blob and its object URL shared by every thumbnail showing it (the list card
// and the detail page show the same primary).

const urlCache = new Map<number, Promise<string>>();

function photoUrl(photoId: number): Promise<string> {
  let p = urlCache.get(photoId);
  if (!p) {
    p = equipmentService.fetchPhotoUrl(photoId);
    p.catch(() => urlCache.delete(photoId));
    urlCache.set(photoId, p);
  }
  return p;
}

function forgetPhoto(photoId: number) {
  const p = urlCache.get(photoId);
  urlCache.delete(photoId);
  p?.then(url => URL.revokeObjectURL(url)).catch(() => {});
}

function usePhotoUrl(photoId: number | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setUrl(null);
    setFailed(false);
    if (!photoId) return;
    let cancelled = false;
    photoUrl(photoId)
      .then(u => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [photoId]);
  return { url, failed };
}

/** A machine's primary photo, or nothing when it has none. */
export function EquipmentPhotoThumb({ photoId, alt, className }: { photoId?: number | null; alt: string; className?: string }) {
  const { url, failed } = usePhotoUrl(photoId);
  if (!photoId) return null;
  return (
    <div className={cn('bg-neutral-100 overflow-hidden flex items-center justify-center shrink-0', className)}>
      {url ? (
        <img src={url} alt={alt} className="w-full h-full object-cover" />
      ) : failed ? (
        <ImageOff className="h-5 w-5 text-neutral-300" />
      ) : (
        <div className="w-full h-full animate-pulse bg-neutral-200" />
      )}
    </div>
  );
}

// ─── Gallery ─────────────────────────────────────────────────────────────────

interface Props {
  /** Absent on a machine not yet created: photos are held in `staged` and uploaded after the save. */
  equipmentId?: number;
  canEdit: boolean;
  staged?: File[];
  onStagedChange?: (files: File[]) => void;
  /** Called when the primary may have changed, so the parent can refresh its header. */
  onChanged?: () => void;
}

export default function EquipmentPhotos({ equipmentId, canEdit, staged = [], onStagedChange, onChanged }: Props) {
  const [photos, setPhotos] = useState<EquipmentPhoto[]>([]);
  const [loading, setLoading] = useState(!!equipmentId);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState<EquipmentPhoto | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const pickRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!equipmentId) return;
    let cancelled = false;
    setLoading(true);
    equipmentService.getPhotos(equipmentId)
      .then(res => { if (!cancelled && res.success && res.data) setPhotos(res.data); })
      .catch(() => { if (!cancelled) setError('Could not load photos.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [equipmentId]);

  async function addFiles(list: FileList | null) {
    const files = Array.from(list ?? []).filter(f => f.type.startsWith('image/') || /\.(heic|jpe?g|png|webp|gif)$/i.test(f.name));
    if (!files.length) return;
    setError('');

    if (!equipmentId) {
      onStagedChange?.([...staged, ...files]);
      return;
    }

    setUploading(n => n + files.length);
    const failed: string[] = [];
    for (const file of files) {
      try {
        const res = await equipmentService.uploadPhoto(equipmentId, file);
        if (res.success && res.data) {
          const added = res.data;
          setPhotos(prev => [...prev.map(p => added.is_primary ? { ...p, is_primary: false } : p), added]);
          if (added.is_primary) onChanged?.();
        } else {
          failed.push(file.name);
        }
      } catch (err) {
        failed.push(err instanceof Error && err.message ? `${file.name} (${err.message})` : file.name);
      } finally {
        setUploading(n => n - 1);
      }
    }
    if (failed.length) setError(`Could not upload ${failed.join(', ')}.`);
  }

  async function makePrimary(photo: EquipmentPhoto) {
    try {
      await equipmentService.setPrimaryPhoto(photo.id);
      setPhotos(prev => prev.map(p => ({ ...p, is_primary: p.id === photo.id })));
      onChanged?.();
    } catch {
      setError('Could not change the main photo.');
    }
  }

  async function remove(photo: EquipmentPhoto) {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await equipmentService.deletePhoto(photo.id);
      forgetPhoto(photo.id);
      setViewing(null);
      setPhotos(prev => {
        const rest = prev.filter(p => p.id !== photo.id);
        // The server promotes the oldest remaining photo; mirror it.
        if (photo.is_primary && rest.length) {
          const oldest = [...rest].sort((a, b) => a.created_at.localeCompare(b.created_at))[0];
          return rest.map(p => ({ ...p, is_primary: p.id === oldest.id }));
        }
        return rest;
      });
      if (photo.is_primary) onChanged?.();
    } catch {
      setError('Could not delete the photo.');
    }
  }

  const ordered = [...photos].sort((a, b) =>
    Number(b.is_primary) - Number(a.is_primary) || a.created_at.localeCompare(b.created_at));
  const empty = !loading && ordered.length === 0 && staged.length === 0 && uploading === 0;

  return (
    <div className="space-y-3">
      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {loading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {[0, 1, 2].map(i => <div key={i} className="aspect-square rounded-lg bg-neutral-100 animate-pulse" />)}
        </div>
      ) : empty ? (
        <p className="text-sm text-neutral-500">
          {canEdit ? 'No photos yet. The first one you add is shown on the equipment list.' : 'No photos yet.'}
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {ordered.map(photo => (
            <button
              key={photo.id}
              type="button"
              onClick={() => setViewing(photo)}
              className="relative aspect-square rounded-lg overflow-hidden border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <EquipmentPhotoThumb photoId={photo.id} alt={photo.caption || photo.file_name || 'Equipment photo'} className="w-full h-full" />
              {photo.is_primary && (
                <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-500" /> Main
                </span>
              )}
            </button>
          ))}
          {staged.map((file, i) => (
            <StagedThumb key={`${file.name}-${i}`} file={file} first={i === 0 && ordered.length === 0}
              onRemove={() => onStagedChange?.(staged.filter((_, j) => j !== i))} />
          ))}
          {Array.from({ length: uploading }).map((_, i) => (
            <div key={`up-${i}`} className="aspect-square rounded-lg bg-neutral-100 flex items-center justify-center">
              <Loader2 className="h-5 w-5 text-neutral-400 animate-spin" />
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => cameraRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <Camera className="h-4 w-4" /> Take photo
          </button>
          <button type="button" onClick={() => pickRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <ImagePlus className="h-4 w-4" /> Choose photos
          </button>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={e => { void addFiles(e.target.files); e.target.value = ''; }} />
          <input ref={pickRef} type="file" accept="image/*" multiple className="hidden"
            onChange={e => { void addFiles(e.target.files); e.target.value = ''; }} />
        </div>
      )}
      {!equipmentId && staged.length > 0 && (
        <p className="text-xs text-neutral-500">Uploaded when the equipment is created.</p>
      )}

      {viewing && (
        <PhotoViewer
          photo={viewing}
          canEdit={canEdit}
          onClose={() => setViewing(null)}
          onMakePrimary={() => { void makePrimary(viewing); setViewing({ ...viewing, is_primary: true }); }}
          onDelete={() => void remove(viewing)}
        />
      )}
    </div>
  );
}

function StagedThumb({ file, first, onRemove }: { file: File; first: boolean; onRemove: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden border border-dashed border-neutral-300 bg-neutral-100">
      {url && <img src={url} alt={file.name} className="w-full h-full object-cover" />}
      {first && (
        <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
          <Star className="h-3 w-3 fill-amber-400 text-amber-500" /> Main
        </span>
      )}
      <button type="button" onClick={onRemove} aria-label={`Remove ${file.name}`}
        className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-neutral-600 hover:text-red-600">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function PhotoViewer({ photo, canEdit, onClose, onMakePrimary, onDelete }: {
  photo: EquipmentPhoto;
  canEdit: boolean;
  onClose: () => void;
  onMakePrimary: () => void;
  onDelete: () => void;
}) {
  const { url, failed } = usePhotoUrl(photo.id);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="flex items-center justify-end gap-2 p-3" onClick={e => e.stopPropagation()}>
        {canEdit && !photo.is_primary && (
          <button type="button" onClick={onMakePrimary}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-sm text-white hover:bg-white/20">
            <Star className="h-4 w-4" /> Make main photo
          </button>
        )}
        {canEdit && (
          <button type="button" onClick={onDelete}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-sm text-red-300 hover:bg-white/20">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
        <button type="button" onClick={onClose} aria-label="Close"
          className="rounded-lg bg-white/10 p-2 text-white hover:bg-white/20">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center px-3 pb-3">
        {url ? (
          <img src={url} alt={photo.caption || photo.file_name || 'Equipment photo'}
            className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} />
        ) : failed ? (
          <p className="text-sm text-neutral-300">Could not load this photo.</p>
        ) : (
          <Loader2 className="h-8 w-8 text-neutral-400 animate-spin" />
        )}
      </div>
      {(photo.caption || photo.uploaded_by_name) && (
        <p className="px-4 pb-4 text-center text-sm text-neutral-300 break-words">
          {photo.caption}{photo.caption && photo.uploaded_by_name ? ' · ' : ''}
          {photo.uploaded_by_name && `Added by ${photo.uploaded_by_name}`}
        </p>
      )}
    </div>
  );
}
